import { CalendarIcon, CheckIcon, ClockIcon, FileTextIcon, SearchIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelfContracts } from "@/hooks/useSelfContracts";
import { cn } from "@/lib/utils";
import type { SelfContract } from "@/types/proto/api/v1/memo_service_pb";
import { useTranslate } from "@/utils/i18n";

interface ContractRecommendationProps {
  ticker: string;
  onSelectContract: (contract: SelfContract) => void;
  onSkip: () => void;
  onOpenSearch: () => void;
  className?: string;
}

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300 dark:border-green-700",
  triggered: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-blue-300 dark:border-blue-700",
  cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 dark:border-gray-600",
  expired: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border-orange-300 dark:border-orange-700",
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
  } catch {
    return dateString;
  }
};

/**
 * ContractRecommendation - Smart contract recommendation component
 *
 * Shows up to 3 active Self-Contracts for the selected ticker, sorted by expiration date.
 * Users can quickly select a contract or skip to manual search.
 */
export const ContractRecommendation: React.FC<ContractRecommendationProps> = ({
  ticker,
  onSelectContract,
  onSkip,
  onOpenSearch,
  className,
}) => {
  const t = useTranslate();
  const [isVisible, setIsVisible] = useState(false);

  // Dynamic status labels
  const CONTRACT_STATUS_LABELS: Record<string, string> = {
    active: t("editor.active"),
    triggered: t("editor.triggered"),
    cancelled: t("editor.cancelled"),
    expired: t("editor.expired"),
  };

  const CONTRACT_TEMPLATE_LABELS: Record<string, string> = {
    "value-oriented": t("editor.selfContractDetail.valueOriented" as any),
    "trading-oriented": t("editor.selfContractDetail.tradingOriented" as any),
  };

  // Fetch active contracts for the ticker
  const {
    data: contractsData,
    isLoading,
    isError,
  } = useSelfContracts({
    ticker: ticker,
    status: "active",
    pageSize: 10, // Fetch more to sort locally
  });

  // Sort contracts by expiration date (earliest first)
  const sortedContracts = (contractsData?.contracts || [])
    .sort((a, b) => {
      if (!a.expiration) return 1;
      if (!b.expiration) return -1;
      return new Date(a.expiration).getTime() - new Date(b.expiration).getTime();
    })
    .slice(0, 3); // Show up to 3 recommendations

  // Show component only when we have ticker and sorted contracts
  useEffect(() => {
    setIsVisible(!!ticker && sortedContracts.length > 0);
  }, [ticker, sortedContracts.length]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={cn("rounded-lg border bg-muted/30 p-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileTextIcon className="w-4 h-4 text-primary" />
          <span className="font-medium text-sm">关联合约推荐</span>
        </div>
        <button
          type="button"
          onClick={onSkip}
          className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title="跳过推荐"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
          <ClockIcon className="w-4 h-4 mr-2 animate-spin" />
          查找合约...
        </div>
      ) : isError ? (
        <div className="py-2 text-sm text-muted-foreground">加载合约失败，请手动搜索</div>
      ) : sortedContracts.length === 0 ? (
        <div className="py-2 text-sm text-muted-foreground">未找到活跃合约，请手动搜索</div>
      ) : (
        <>
          {/* Recommended contracts list */}
          <div className="space-y-2 mb-3">
            {sortedContracts.map((contract) => (
              <ContractCard
                key={contract.contractId}
                contract={contract}
                statusLabels={CONTRACT_STATUS_LABELS}
                templateLabels={CONTRACT_TEMPLATE_LABELS}
                t={t}
                onSelect={() => onSelectContract(contract)}
              />
            ))}
          </div>

          {/* Footer: Manual search option */}
          <button
            type="button"
            onClick={onOpenSearch}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
          >
            <SearchIcon className="w-3.5 h-3.5" />
            {t("editor.contract.manualSearch" as any)}
          </button>
        </>
      )}
    </div>
  );
};

interface ContractCardProps {
  contract: SelfContract;
  statusLabels: Record<string, string>;
  templateLabels: Record<string, string>;
  t: (key: any) => string;
  onSelect: () => void;
}

const ContractCard: React.FC<ContractCardProps> = ({ contract, statusLabels, templateLabels, t, onSelect }) => {
  const statusColor = statusColors[contract.status] || statusColors.active;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full flex items-start gap-3 p-3 rounded-md bg-background border hover:border-primary/50 hover:bg-accent/50 transition-all text-left"
    >
      <div className="flex-1 min-w-0">
        {/* Header: Template + Status */}
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">
            {templateLabels[contract.template] || contract.template}
          </span>
          <span className={cn("text-xs px-1.5 py-0.5 rounded border", statusColor)}>
            {statusLabels[contract.status] || contract.status}
          </span>
        </div>

        {/* Expiration */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <CalendarIcon className="w-3 h-3" />
          <span>
            {t("editor.contract.expires" as any)}: {formatDate(contract.expiration)}
          </span>
        </div>

        {/* Idea (truncated) */}
        {contract.idea && <p className="text-xs text-muted-foreground line-clamp-1">{contract.idea}</p>}
      </div>

      {/* Select button */}
      <CheckIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
    </button>
  );
};

export default ContractRecommendation;
